import { inject } from '@angular/core';
import { CompanyApi } from '@app/api/company.api';
import { CompanyDto, UpdateCompanyRequest } from '@app/models';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';

interface InitialState {
  company: CompanyDto | null;
  isLoading: boolean;
}

const initialState: InitialState = {
  company: null,
  isLoading: false,
};

export const CompanyStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withMethods((store) => {
    const companyApi = inject(CompanyApi);

    const loadCompany = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { isLoading: true })),
        switchMap(() => companyApi.getCompanyDetails$()),
        tap((company) => patchState(store, { company, isLoading: false })),
      ),
    );

    const updateCompany = rxMethod<{
      company: UpdateCompanyRequest;
      onSubmit: () => void;
      onSuccess: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isLoading: true })),
        switchMap(({ company, onSubmit, onSuccess }) =>
          companyApi.updateCompany$(company).pipe(
            tap((updatedCompany) => {
              patchState(store, (state) => ({
                company:
                  state.company?.id === updatedCompany.id
                    ? updatedCompany
                    : state.company,
                isLoading: false,
              }));

              onSubmit();
              onSuccess();
            }),
          ),
        ),
      ),
    );

    const clearCompany = () => patchState(store, initialState);

    return {
      loadCompany,
      updateCompany,
      clearCompany,
    };
  }),
);
